import { useMemo } from 'react';

import {
  useWrappedQuery,
  useWrappedQueryPrefetch,
  useWrappedShareQuery,
} from './growth.query';

import type {
  GrowthData,
  HeatmapCell,
  LittlebirdUsageData,
  RhythmData,
  TopToolData,
  WrappedReportData,
  WrappedResponse,
} from './growth.types';

function buildHeatmapWeeks(
  dailyActivity: WrappedResponse['daily_activity'],
  periodStart: string
): (HeatmapCell | null)[][] {
  const nonZero = dailyActivity
    .map((d) => d.frame_count)
    .filter((c) => c > 0)
    .sort((a, b) => a - b);
  const q1 = nonZero[Math.floor(nonZero.length * 0.25)] ?? 1;
  const q2 = nonZero[Math.floor(nonZero.length * 0.5)] ?? 1;
  const q3 = nonZero[Math.floor(nonZero.length * 0.75)] ?? 1;

  const toLevel = (count: number): 0 | 1 | 2 | 3 | 4 =>
    count === 0 ? 0 : count <= q1 ? 1 : count <= q2 ? 2 : count <= q3 ? 3 : 4;

  const [yy, mm, dd] = periodStart.split('-').map(Number);
  const leadingBlanks = new Date(Date.UTC(yy, mm - 1, dd)).getUTCDay();

  const cells: (HeatmapCell | null)[] = [
    ...Array.from({ length: leadingBlanks }, () => null),
    ...dailyActivity.map((d) => ({
      date: d.date,
      level: toLevel(d.frame_count),
    })),
  ];
  while (cells.length % 7 !== 0) cells.push(null);

  const weeks: (HeatmapCell | null)[][] = [];
  for (let i = 0; i < cells.length; i += 7) weeks.push(cells.slice(i, i + 7));
  return weeks;
}

function pickPeakDay(
  dailyActivity: WrappedResponse['daily_activity']
): string | null {
  let best: { date: string; count: number } | null = null;
  for (const d of dailyActivity) {
    if (d.frame_count <= 0) continue;
    if (!best || d.frame_count > best.count) {
      best = { date: d.date, count: d.frame_count };
    }
  }
  return best?.date ?? null;
}

const DEFAULT_LITTLEBIRD: LittlebirdUsageData = {
  chatCount: 0,
  messageCount: 0,
  longestChatMessages: 0,
  meetingsRecorded: 0,
  todosCreated: 0,
  journalsCreated: 0,
  daysOpened: 0,
};

const DEFAULT_RHYTHM: RhythmData = {
  peakHourLocal: null,
  hourPersona: 'explorer',
  weekendShare: 0,
  weekdayVsWeekend: 'balanced',
};

const DEFAULT_GROWTH: GrowthData = {
  aiCapturesGrowth: 1,
  chatCountGrowth: 1,
  toolsNewThisMonth: [],
  showMultiplier: false,
};

function transformTopTool(data: WrappedResponse): TopToolData | null {
  if (!data.top_tool) return null;
  return {
    name: data.top_tool.name,
    captureCount: data.top_tool.capture_count,
    sharePct: data.top_tool.share_of_ai,
    firstSeen: data.top_tool.first_seen,
  };
}

function transformLittlebird(data: WrappedResponse): LittlebirdUsageData {
  const lb = data.littlebird_usage;
  if (!lb) return DEFAULT_LITTLEBIRD;
  return {
    chatCount: lb.chat_count,
    messageCount: lb.message_count,
    longestChatMessages: lb.longest_chat_messages,
    meetingsRecorded: lb.meetings_recorded,
    todosCreated: lb.todos_created,
    journalsCreated: lb.journals_created,
    daysOpened: lb.days_opened,
  };
}

function transformRhythm(data: WrappedResponse): RhythmData {
  const r = data.rhythm;
  if (!r) return DEFAULT_RHYTHM;
  return {
    peakHourLocal: r.peak_hour_local,
    hourPersona: r.hour_persona,
    weekendShare: r.weekend_share,
    weekdayVsWeekend: r.weekday_vs_weekend,
  };
}

function transformGrowth(data: WrappedResponse): GrowthData {
  const g = data.growth;
  if (!g) return DEFAULT_GROWTH;
  return {
    aiCapturesGrowth: g.ai_captures_growth,
    chatCountGrowth: g.chat_count_growth,
    toolsNewThisMonth: g.tools_new_this_month,
    showMultiplier: g.show_multiplier,
  };
}

function transformResponse(data: WrappedResponse): WrappedReportData {
  return {
    periodLabel: data.period_label,
    headline: data.headline,
    persona: data.persona,
    topPercentile: data.estimated_top_percentile ?? null,
    ownerName: data.owner_name ?? null,
    topTool: transformTopTool(data),
    tools: data.ai_tools.slice(0, 5).map((t) => ({
      name: t.name,
      percentage: t.percentage,
      captureCount: t.frame_count,
    })),
    daysActive: data.days_active,
    totalDays: data.total_days,
    longestStreak: data.longest_streak,
    currentStreak: data.current_streak,
    peakDayDate: pickPeakDay(data.daily_activity),
    heatmapWeeks: buildHeatmapWeeks(data.daily_activity, data.period_start),
    littlebirdUsage: transformLittlebird(data),
    rhythm: transformRhythm(data),
    growth: transformGrowth(data),
  };
}

const WRAPPED_PERIOD_START = '2026-04-01';
const WRAPPED_PERIOD_END = '2026-04-30';

export const useWrappedReport = () => {
  const {
    data: raw,
    isLoading,
    error,
  } = useWrappedQuery(WRAPPED_PERIOD_START, WRAPPED_PERIOD_END);
  const data = useMemo(() => (raw ? transformResponse(raw) : null), [raw]);

  return { data, isLoading, error };
};

export const useWrappedReportPrefetch = () => {
  useWrappedQueryPrefetch(WRAPPED_PERIOD_START, WRAPPED_PERIOD_END);
};

export const useWrappedReportByShareId = (shareId: string) => {
  const { data: raw, isLoading, error } = useWrappedShareQuery(shareId);
  const data = useMemo(() => (raw ? transformResponse(raw) : null), [raw]);
  return { data, isLoading, error };
};
